import { Grid } from '../../core/Grid.js';
import { Direction, Position, Point, pointKey, pointsEqual, parsePointKey, isRimEdge, oppositeDir, directionBetween } from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';
import { stripSeparators } from '../stateSerialization.js';

export interface DigitTarget {
    digit: number;
    cellPosition: Position;
    corners: Point[];  // Corner points that are targets (internal corners only)
}

export type ViolationKind = 'branching-path' | 'branching-jump' | 'rim-vortex';

export interface TraceResult {
    path: Point[];                          // full path of points (for rendering)
    tracedSegmentKeys: Set<string>;         // placed segment keys in the trace (teal)
    violationKeys: Set<string>;             // placed segment keys with violations (red)
    tip: Point;                             // final position
    /** The line ran off the rim with every digit cleared in order. */
    reachedEnd: boolean;
    /** reachedEnd, and no segment left stranded off the path. */
    won: boolean;
    clearedDigits: number[];
    outOfOrderDigits: number[];
    hitCorners: Set<string>;
    lastTargetPerDigit: Map<number, Point>;
    nextTarget: number | 'exit' | null;
    cornerCount: number;
    cornerPoints: Set<string>;              // grid points where the path turns (scored corners)
    gridSegmentCount: number;
    rimJumpHint: Point | null;              // teal wrap hint (if stopped after rim-jump)
    violationType: ViolationKind | null;    // type of violation that stopped the trace
    gateJumps: Array<[Point, Point]>;       // gate jump segments (entry → exit)
    mirroredSegmentKeys: Set<string>;       // mirrored gate segments (dashed teal)
    disconnectedSegmentKeys: Set<string>;   // placed but never traced (amber)
}

export function segmentKey(from: Point, to: Point): string {
    const k1 = pointKey(from);
    const k2 = pointKey(to);
    return k1 < k2 ? k1 + k2 : k2 + k1;
}

export function parseSegmentKey(key: string): [Point, Point] {
    const p1 = parsePointKey(key.slice(0, 2));
    const p2 = parsePointKey(key.slice(2, 4));
    return [p1, p2];
}


/** Get the adjacent point in a direction */
function neighborPoint(p: Point, dir: Direction): Point {
    switch (dir) {
        case 'N': return { row: p.row - 1, col: p.col };
        case 'S': return { row: p.row + 1, col: p.col };
        case 'W': return { row: p.row, col: p.col - 1 };
        case 'E': return { row: p.row, col: p.col + 1 };
    }
}

// Segment index: for each point, which segments go in which direction
interface SegmentIndex {
    // pointKey → Map<Direction, { segKey, next }>
    byPointDir: Map<string, Map<Direction, { segKey: string; next: Point }>>;
}

function buildSegmentIndex(segments: Set<string>): SegmentIndex {
    const byPointDir = new Map<string, Map<Direction, { segKey: string; next: Point }>>();

    for (const segKey of segments) {
        const [p1, p2] = parseSegmentKey(segKey);
        const dir = directionBetween(p1, p2);
        if (!dir) continue; // not adjacent (shouldn't happen for valid segments)

        const k1 = pointKey(p1);
        const k2 = pointKey(p2);

        if (!byPointDir.has(k1)) byPointDir.set(k1, new Map());
        byPointDir.get(k1)!.set(dir, { segKey, next: p2 });

        if (!byPointDir.has(k2)) byPointDir.set(k2, new Map());
        byPointDir.get(k2)!.set(oppositeDir(dir), { segKey, next: p1 });
    }

    return { byPointDir };
}

export class LineGame {
    protected grid: Grid;
    private startPoint: Point | null = null;
    protected segments: Set<string> = new Set();
    private digitTargets: Map<number, DigitTarget> = new Map();

    protected get gs() { return this.grid.serializer; }

    constructor(grid: Grid) {
        this.grid = grid;
        this.initializeDigitTargets();
        this.initializeStartPoint();
    }

    private initializeStartPoint(): void {
        const digit1 = this.digitTargets.get(this.grid.minDigit);
        if (!digit1) return;

        const pos = digit1.cellPosition;
        const centerRow = pos.row + 0.5;
        const centerCol = pos.col + 0.5;

        const candidates = this.grid.rimPoints();

        let best: Point = candidates[0];
        let bestDist = Infinity;
        for (const p of candidates) {
            const dist = Math.abs(p.row - centerRow) + Math.abs(p.col - centerCol);
            if (dist < bestDist) {
                bestDist = dist;
                best = p;
            }
        }

        this.startPoint = best;
    }

    private initializeDigitTargets(): void {
        const positions = this.grid.getAllDigitPositions();

        for (const [digit, pos] of positions) {
            const corners: Point[] = [];
            const allCorners: Point[] = [
                { row: pos.row, col: pos.col },
                { row: pos.row, col: pos.col + 1 },
                { row: pos.row + 1, col: pos.col },
                { row: pos.row + 1, col: pos.col + 1 }
            ];

            for (const corner of allCorners) {
                if (corner.row > 0 && corner.row < this.grid.size &&
                    corner.col > 0 && corner.col < this.grid.size) {
                    corners.push(corner);
                }
            }

            this.digitTargets.set(digit, { digit, cellPosition: pos, corners });
        }
    }

    getGrid(): Grid { return this.grid; }
    getStartPoint(): Point | null { return this.startPoint; }
    getSegments(): Set<string> { return this.segments; }
    isStarted(): boolean { return this.segments.size > 0; }
    getDigitTargets(): Map<number, DigitTarget> { return this.digitTargets; }
    isOnGridEdge(p: Point): boolean { return this.grid.isRimPoint(p); }

    isWrapSegment(from: Point, to: Point): boolean {
        const wrapped = this.grid.getWrapPoint(from);
        return wrapped !== null && pointsEqual(wrapped, to);
    }

    setStartPoint(point: Point): boolean {
        if (!this.isOnGridEdge(point)) return false;
        this.startPoint = { ...point };
        return true;
    }

    hasSegment(from: Point, to: Point): boolean {
        return this.segments.has(segmentKey(from, to));
    }

    toggleSegment(from: Point, to: Point): boolean {
        if (this.grid.isRimPoint(from) && this.grid.isRimPoint(to)) {
            const edge = this.grid.createEdge(from, to);
            if (edge && isRimEdge(edge)) return false;
        }

        const segKey = segmentKey(from, to);
        if (this.segments.has(segKey)) {
            this.segments.delete(segKey);
        } else {
            this.segments.add(segKey);
        }
        return true;
    }

    // ── Tracing ──────────────────────────────────────────────────

    trace(): TraceResult {
        const empty: TraceResult = {
            path: this.startPoint ? [this.startPoint] : [],
            tracedSegmentKeys: new Set(),
            violationKeys: new Set(),
            tip: this.startPoint || { row: 0, col: 0 },
            reachedEnd: false,
            won: false,
            clearedDigits: [],
            outOfOrderDigits: [],
            hitCorners: new Set(),
            lastTargetPerDigit: new Map(),
            nextTarget: this.startPoint ? this.grid.minDigit : null,
            cornerCount: 0,
            cornerPoints: new Set(),
            gridSegmentCount: 0,
            rimJumpHint: null,
            violationType: null,
            gateJumps: [],
            mirroredSegmentKeys: new Set(),
            disconnectedSegmentKeys: new Set(),
        };

        if (!this.startPoint || this.segments.size === 0) return empty;

        const index = buildSegmentIndex(this.segments);
        return this.finalize(this.runTrace(this.startPoint, index));
    }

    /**
     * Settle the fields that can only be known once the whole trace is in: which
     * segments were left stranded, and so whether this is really a win. LineVortex
     * runs this again after it works out its mirrored segments, which are placed
     * but never traced and so must not read as stranded.
     */
    protected finalize(result: TraceResult): TraceResult {
        result.disconnectedSegmentKeys = new Set(
            [...this.segments].filter(key =>
                !result.tracedSegmentKeys.has(key)
                && !result.mirroredSegmentKeys.has(key)
                && !result.violationKeys.has(key)
            )
        );
        result.won = result.reachedEnd && result.disconnectedSegmentKeys.size === 0;
        return result;
    }

    protected runTrace(start: Point, index: SegmentIndex): TraceResult {
        const path: Point[] = [start];
        const tracedSegmentKeys = new Set<string>();
        const violationKeys = new Set<string>();
        const usedSegments = new Set<string>();

        // Target tracking
        const hitCornersPerDigit = new Map<number, Set<string>>();
        const lastCornerPerDigit = new Map<number, Point>();
        const hitCorners = new Set<string>();
        const clearedDigits: number[] = [];

        for (const [digit] of this.digitTargets) {
            hitCornersPerDigit.set(digit, new Set());
        }

        let tip = start;
        let lastDirection: Direction | null = null;
        let rimJumped = false;
        // The line hasn't travelled yet — it is still sitting at its entry into the
        // grid. Suspends the rim-jump and win checks, both of which would otherwise
        // fire immediately since a start point is always a rim point. A gate jump
        // does not clear it: a gate at the origin moves where the line enters, it is
        // not the line moving (see Step 1).
        let beforeFirstMove = true;
        let cornerCount = 0;
        const cornerPoints = new Set<string>();
        let gridSegmentCount = 0;
        let reachedEnd = false;
        let rimJumpHint: Point | null = null;
        let justRimJumped = false;
        let violationType: ViolationKind | null = null;
        const gateJumps: Array<[Point, Point]> = [];

        // Check targets at start point
        this.checkTargetHits(tip, hitCornersPerDigit, lastCornerPerDigit, hitCorners, clearedDigits);

        while (true) {
            // Step 0: Flag resets
            if (!this.grid.isRimPoint(tip)) {
                rimJumped = false;
            }
            this.resetGateFlags(tip);

            // Step 1: Gate-jump check (base game has no gates). Unlike the rim-jump and
            // win checks below, this also runs before the line's first move: a start
            // point on an active gate's corner is caught by that gate, exactly as the
            // same corner would catch the line later on. The jump relocates the entry
            // point rather than counting as travel, so `beforeFirstMove` survives it —
            // the line enters the grid at the paired corner as if it had started there.
            {
                const gateResult = this.checkGateJump(tip, lastDirection, index, usedSegments);
                if (gateResult) {
                    if (gateResult.violation) {
                        violationType = gateResult.violation;
                        if (gateResult.violationSegKey) {
                            violationKeys.add(gateResult.violationSegKey);
                        }
                        if (gateResult.violation === 'branching-jump' && tracedSegmentKeys.size > 0) {
                            // Mark the last traced segment
                            const lastSeg = [...tracedSegmentKeys].pop()!;
                            violationKeys.add(lastSeg);
                        }
                        break;
                    }
                    // Successful gate jump — reset direction so all exits are available
                    gateJumps.push([tip, gateResult.exitCorner]);
                    path.push(gateResult.exitCorner);
                    tip = gateResult.exitCorner;
                    lastDirection = null;
                    justRimJumped = false;
                    this.checkTargetHits(tip, hitCornersPerDigit, lastCornerPerDigit, hitCorners, clearedDigits);
                    continue;
                }
            }

            // Step 2: Win check (not before the line's first move)
            if (!beforeFirstMove && this.grid.isRimPoint(tip) && clearedDigits.length >= this.grid.digitCount) {
                reachedEnd = true;
                break;
            }

            // Step 3: Rim-jump check (not before the line's first move)
            if (!beforeFirstMove && this.grid.isRimPoint(tip) && !rimJumped) {
                const wrapped = this.grid.getWrapPoint(tip);
                if (wrapped) {
                    path.push(wrapped);
                    tip = wrapped;
                    rimJumped = true;
                    justRimJumped = true;
                    // lastDirection preserved
                    this.checkTargetHits(tip, hitCornersPerDigit, lastCornerPerDigit, hitCorners, clearedDigits);
                    continue;
                }
            }

            // Step 4: Grid-edge scan
            const candidates = this.findGridCandidates(tip, lastDirection, index, usedSegments);

            if (candidates.length === 0) {
                // If we just rim-jumped and stopped, set the hint
                if (justRimJumped) {
                    rimJumpHint = tip;
                }
                break;
            }

            if (candidates.length > 1) {
                // Branching-path violation: mark all candidates
                violationType = 'branching-path';
                for (const c of candidates) {
                    violationKeys.add(c.segKey);
                }
                break;
            }

            // Single candidate: follow it
            const { segKey, next, dir } = candidates[0];
            usedSegments.add(segKey);
            tracedSegmentKeys.add(segKey);
            path.push(next);
            tip = next;
            beforeFirstMove = false;
            justRimJumped = false;

            // Corner counting (the vertex is the point we just turned at)
            if (lastDirection !== null && dir !== lastDirection) {
                cornerCount++;
                cornerPoints.add(pointKey(path[path.length - 2]));
            }
            lastDirection = dir;
            gridSegmentCount++;

            // Check target hits at new position
            this.checkTargetHits(tip, hitCornersPerDigit, lastCornerPerDigit, hitCorners, clearedDigits);
            this.checkMirroredTargetHits(segKey, hitCornersPerDigit, lastCornerPerDigit, hitCorners, clearedDigits);
        }

        // Compute out-of-order digits
        const outOfOrderDigits = this.computeOutOfOrder(hitCornersPerDigit, clearedDigits);

        // Compute next target
        const nextTarget = this.computeNextTarget(clearedDigits);

        // Compute lastTargetPerDigit (only for cleared digits)
        const lastTargetPerDigit = new Map<number, Point>();
        for (const digit of clearedDigits) {
            const pt = lastCornerPerDigit.get(digit);
            if (pt) lastTargetPerDigit.set(digit, pt);
        }

        return {
            path, tracedSegmentKeys, violationKeys, tip, reachedEnd,
            // Settled by finalize() once the trace is complete.
            won: false,
            clearedDigits, outOfOrderDigits, hitCorners,
            lastTargetPerDigit, nextTarget,
            cornerCount, cornerPoints, gridSegmentCount, rimJumpHint,
            violationType, gateJumps,
            mirroredSegmentKeys: new Set(),
            disconnectedSegmentKeys: new Set(),
        };
    }

    /** Find grid segment candidates at tip (non-backward, non-used) */
    protected findGridCandidates(
        tip: Point,
        lastDirection: Direction | null,
        index: SegmentIndex,
        usedSegments: Set<string>,
    ): Array<{ segKey: string; next: Point; dir: Direction }> {
        const candidates: Array<{ segKey: string; next: Point; dir: Direction }> = [];
        const dirMap = index.byPointDir.get(pointKey(tip));
        if (!dirMap) return candidates;

        const backDir = lastDirection ? oppositeDir(lastDirection) : null;

        for (const [dir, entry] of dirMap) {
            if (dir === backDir) continue;
            if (usedSegments.has(entry.segKey)) continue;
            candidates.push({ segKey: entry.segKey, next: entry.next, dir });
        }

        return candidates;
    }

    /** Check target corner hits at a point, updating tracking state */
    protected checkTargetHits(
        point: Point,
        hitCornersPerDigit: Map<number, Set<string>>,
        lastCornerPerDigit: Map<number, Point>,
        hitCorners: Set<string>,
        clearedDigits: number[],
    ): void {
        const pk = pointKey(point);
        for (const [digit, target] of this.digitTargets) {
            for (const corner of target.corners) {
                if (pointsEqual(corner, point)) {
                    hitCorners.add(pk);
                    hitCornersPerDigit.get(digit)!.add(pk);
                    lastCornerPerDigit.set(digit, { ...point });

                    const hitCount = hitCornersPerDigit.get(digit)!.size;
                    if (hitCount === target.corners.length && !clearedDigits.includes(digit)) {
                        if (clearedDigits.length === digit - this.grid.minDigit) {
                            clearedDigits.push(digit);
                        }
                    }
                }
            }
        }
    }

    private computeOutOfOrder(
        hitCornersPerDigit: Map<number, Set<string>>,
        clearedDigits: number[],
    ): number[] {
        const result: number[] = [];
        for (const [digit, target] of this.digitTargets) {
            const hitCount = hitCornersPerDigit.get(digit)!.size;
            if (hitCount === target.corners.length && !clearedDigits.includes(digit)) {
                result.push(digit);
            }
        }
        return result.sort((a, b) => a - b);
    }

    private computeNextTarget(clearedDigits: number[]): number | 'exit' | null {
        const nextDigit = clearedDigits.length + this.grid.minDigit;
        if (nextDigit <= this.grid.maxDigit) return nextDigit;
        if (clearedDigits.length >= this.grid.digitCount) return 'exit';
        return null;
    }

    // Gate/mirror hooks (overridden by LineVortex)
    protected checkMirroredTargetHits(
        _segKey: string,
        _hitCornersPerDigit: Map<number, Set<string>>,
        _lastCornerPerDigit: Map<number, Point>,
        _hitCorners: Set<string>,
        _clearedDigits: number[],
    ): void { /* base has no mirrors */ }
    protected resetGateFlags(_tip: Point): void { /* base has no gates */ }
    protected checkGateJump(
        _tip: Point,
        _lastDirection: Direction | null,
        _index: SegmentIndex,
        _usedSegments: Set<string>,
    ): { exitCorner: Point; violation?: ViolationKind; violationSegKey?: string } | null {
        return null; // base has no gates
    }

    // ── Derived methods (thin wrappers over trace) ───────────────

    getHitCorners(): Set<string> { return this.trace().hitCorners; }
    getClearedDigits(): number[] { return this.trace().clearedDigits; }
    getOutOfOrderDigits(): number[] { return this.trace().outOfOrderDigits; }
    getLastTargetPerDigit(): Map<number, Point> { return this.trace().lastTargetPerDigit; }

    checkWin(): boolean { return this.trace().won; }

    isComplete(): boolean { return this.trace().won; }

    hasViolations(): boolean { return this.trace().violationKeys.size > 0; }

    // ── Hints (newcomer aid; mirror Branches) ────────────────────

    /** Non-corner rim intersections where the start can be (re)placed — the
     *  start-relocation hint, shown before any segment is drawn. Excludes the
     *  current start (it already carries the pin marker). */
    getStartHintPoints(): Point[] {
        if (this.isStarted()) return [];
        return this.grid.rimPoints().filter(p => !this.startPoint || !pointsEqual(p, this.startPoint));
    }

    /**
     * The rim intersection whose trace follows the most placed segments — the
     * "ghost start". It surfaces where the drawn line actually meets the rim when
     * the current start has been nudged off it (a common mis-click when placing a
     * rim-touching edge). Null when the current start is already the best (the usual
     * case, so no ghost shows) or nothing is drawn. Cheap: starts with no attached
     * segment trace nothing and return immediately.
     */
    getGhostStartPoint(): Point | null {
        if (this.segments.size === 0) return null;
        const index = buildSegmentIndex(this.segments);

        // Rank a start by (digits cleared in order, then segments followed). The
        // order term is what stops the measure "at the first red diamond": running
        // your own line backwards (a mis-click onto its end) traces just as many
        // segments but clears nothing in sequence, so it loses to the true start.
        const rank = (p: Point): [number, number] => {
            const r = this.runTrace(p, index);
            return [r.clearedDigits.length, r.tracedSegmentKeys.size];
        };
        const better = (a: [number, number], b: [number, number]) =>
            a[0] !== b[0] ? a[0] > b[0] : a[1] > b[1];

        let bestRank: [number, number] = this.startPoint ? rank(this.startPoint) : [0, 0];
        let best: Point | null = null;
        for (const p of this.grid.rimPoints()) {
            if (this.startPoint && pointsEqual(p, this.startPoint)) continue;
            const r = rank(p);
            if (better(r, bestRank)) { bestRank = r; best = p; }
        }
        return best;
    }

    /** Number of placed segments meeting at a point (interior degree). */
    private pointDegree(p: Point): number {
        let d = 0;
        for (const dir of ['N', 'S', 'E', 'W'] as Direction[]) {
            if (this.hasSegment(p, neighborPoint(p, dir))) d++;
        }
        return d;
    }

    /** Directions the traced path can extend from its tip — the next-segment hint.
     *  Works from the very start too (the tip is the start point when nothing is
     *  drawn yet), so the first move is hinted in front of the start.
     *
     *  `free` is true when joining there keeps a simple path — the target point has
     *  at most one segment, so the new segment continues or links a stray run (drawn
     *  teal). It's false only when the target already has two segments, so a third
     *  would fork it into a branching point (drawn amber). */
    getSegmentHints(): Array<{ from: Point; to: Point; free: boolean }> {
        if (!this.startPoint) return [];
        const result = this.trace();
        if (result.won) return [];
        const tip = result.tip;
        if (!tip || this.pointDegree(tip) >= 2) return [];

        const size = this.grid.size;
        const hints: Array<{ from: Point; to: Point; free: boolean }> = [];
        for (const dir of ['N', 'S', 'E', 'W'] as Direction[]) {
            const nb = neighborPoint(tip, dir);
            if (nb.row < 0 || nb.row > size || nb.col < 0 || nb.col > size) continue; // off-grid
            if (this.hasSegment(tip, nb)) continue;                                    // already placed
            // A segment can't run along the rim.
            if (this.grid.isRimPoint(tip) && this.grid.isRimPoint(nb)) {
                const edge = this.grid.createEdge(tip, nb);
                if (edge && isRimEdge(edge)) continue;
            }
            hints.push({ from: tip, to: nb, free: this.pointDegree(nb) <= 1 });
        }
        return hints;
    }

    /** Scoring-rules overlay: corners (primary, amber dots) + the line segments
     *  (secondary, blue). */
    getScoringHighlight(): ScoringHighlight {
        const result = this.trace();
        return {
            primaryPoints: [...result.cornerPoints].map(parsePointKey),
            secondaryEdges: [...this.segments].map(parseSegmentKey),
        };
    }

    getScore(): ScoreBreakdown | null {
        const result = this.trace();
        if (!result.won) return null;
        return calculateScore(result.cornerCount, result.gridSegmentCount);
    }

    reset(): void {
        this.segments.clear();
        this.initializeStartPoint();
    }

    /**
     * Jumpstart a solution: draw a line that clears the first digit. The line
     * enters from a rim, runs straight in to one of the digit's target corners,
     * then traces around the rest. It stays interior (so no rim-wrap) and forms
     * a single non-branching path. All four rim approaches are tried; the
     * shortest one that cleanly clears the digit (no branch, nothing completed
     * out of order) is kept — so a border-hugging digit gets a short line in
     * from the near edge rather than a long drop from the top.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const target = this.digitTargets.get(this.grid.minDigit);
        if (!target || target.corners.length === 0) return false;

        // Bounding rectangle of the digit's interior target corners.
        let cL = Infinity, cR = -Infinity, rT = Infinity, rB = -Infinity;
        for (const c of target.corners) {
            cL = Math.min(cL, c.col); cR = Math.max(cR, c.col);
            rT = Math.min(rT, c.row); rB = Math.max(rB, c.row);
        }
        const rect = { cL, cR, rT, rB };
        const cols = cR > cL ? [cL, cR] : [cL];
        const rows = rB > rT ? [rT, rB] : [rT];

        // Candidate approaches: down/up a target column, or in from left/right
        // along a target row, each entering at a target corner.
        const candidates: Point[][] = [];
        for (const ec of cols) {
            candidates.push(this.approach('top', rect, rT, ec));
            candidates.push(this.approach('bottom', rect, rB, ec));
        }
        for (const er of rows) {
            candidates.push(this.approach('left', rect, er, cL));
            candidates.push(this.approach('right', rect, er, cR));
        }

        let best: Point[] | null = null;        // shortest clean path
        let fallback: Point[] | null = null;    // shortest path that at least clears
        for (const pts of candidates) {
            this.applyLinePath(pts);
            const t = this.trace();
            if (!t.clearedDigits.includes(this.grid.minDigit)) continue;
            if (!fallback || pts.length < fallback.length) fallback = pts;
            if (t.violationKeys.size === 0 && t.outOfOrderDigits.length === 0) {
                if (!best || pts.length < best.length) best = pts;
            }
        }

        const chosen = best ?? fallback;
        if (chosen) {
            this.applyLinePath(chosen);
            return true;
        }

        this.reset();
        return false;
    }

    /**
     * Build a candidate point path: a straight run from the given rim into the
     * entry corner (entryRow, entryCol), then a tour of the remaining target
     * corners (the 3-edge "U" of a square, or a single domino edge).
     */
    private approach(
        side: 'top' | 'bottom' | 'left' | 'right',
        rect: { cL: number; cR: number; rT: number; rB: number },
        entryRow: number,
        entryCol: number,
    ): Point[] {
        const { cL, cR, rT, rB } = rect;
        const size = this.grid.size;
        const pts: Point[] = [];

        switch (side) {
            case 'top': for (let r = 0; r <= entryRow; r++) pts.push({ row: r, col: entryCol }); break;
            case 'bottom': for (let r = size; r >= entryRow; r--) pts.push({ row: r, col: entryCol }); break;
            case 'left': for (let c = 0; c <= entryCol; c++) pts.push({ row: entryRow, col: c }); break;
            case 'right': for (let c = size; c >= entryCol; c--) pts.push({ row: entryRow, col: c }); break;
        }

        const otherCol = entryCol === cL ? cR : cL;
        const otherRow = entryRow === rT ? rB : rT;
        if (cR > cL && rB > rT) {
            pts.push({ row: entryRow, col: otherCol });
            pts.push({ row: otherRow, col: otherCol });
            pts.push({ row: otherRow, col: entryCol });
        } else if (cR > cL) {
            pts.push({ row: entryRow, col: otherCol });
        } else if (rB > rT) {
            pts.push({ row: otherRow, col: entryCol });
        }
        return pts;
    }

    /** Reset and lay down a point path as line segments, with the rim entry. */
    private applyLinePath(pts: Point[]): void {
        this.reset();
        if (pts.length === 0) return;
        this.startPoint = { ...pts[0] };
        for (let i = 0; i + 1 < pts.length; i++) {
            this.toggleSegment(pts[i], pts[i + 1]);
        }
    }

    getStatus(): string {
        const result = this.trace();
        const boldRed = (text: string) =>
            `<span style="color:#e94560;font-weight:bold">${text}</span>`;
        const boldAmber = (text: string) =>
            `<span style="color:#fbbf24;font-weight:bold">${text}</span>`;

        if (result.won) {
            return 'You win! Line completed through all digits in order!';
        }

        // Violations break the line's structure outright — red, like the segments.
        if (result.violationKeys.size > 0) {
            switch (result.violationType) {
                case 'branching-jump':
                    return `${boldRed('Invalid')}: multiple gate jumps possible. Deactivate conflicting gates.`;
                case 'rim-vortex':
                    return `${boldRed('Invalid')}: gate mirror conflicts with the grid rim.`;
                default:
                    return `${boldRed('Invalid')}: line branches. Remove segments at crossing points.`;
            }
        }

        // Red to match the red diamond the early digit's last target now carries.
        if (result.outOfOrderDigits.length > 0) {
            // A ghost start means some rim point runs the line better than the current
            // start — with an out-of-order warning too, that's most likely the line
            // being traced backwards after the start was nudged off its end.
            if (this.getGhostStartPoint() !== null) {
                return `${boldRed('Warning')}: you may have moved your start point by mistake.`;
            }
            const digits = result.outOfOrderDigits.join(', ');
            const plural = result.outOfOrderDigits.length > 1;
            return `${boldRed('Warning')}: digit${plural ? 's' : ''} ${digits} ` +
                `${plural ? 'were' : 'was'} completed too early.`;
        }

        // Only worth raising once the line is otherwise home: stray segments are a
        // normal part of building, but they can't be left behind in a finished board.
        if (result.reachedEnd && result.disconnectedSegmentKeys.size > 0) {
            const count = result.disconnectedSegmentKeys.size;
            return `${boldAmber('Warning')}: ${count} disconnected segment${count > 1 ? 's' : ''}. ` +
                `Remove ${count > 1 ? 'them' : 'it'} to finish.`;
        }

        if (result.nextTarget === 'exit') {
            return 'All digits cleared! Connect to a grid edge to finish.';
        }

        if (typeof result.nextTarget === 'number') {
            const target = this.digitTargets.get(result.nextTarget);
            if (target) {
                const hitCount = target.corners.filter(c =>
                    result.hitCorners.has(pointKey(c))
                ).length;
                return `Clearing digit ${result.nextTarget}: ${hitCount}/${target.corners.length} targets. Click edges to draw.`;
            }
        }

        return 'Click on edges to add/remove line segments.';
    }

    // ── Serialization ────────────────────────────────────────────

    serialize(): string {
        const parts: string[] = [];
        if (this.startPoint) {
            parts.push(pointKey(this.startPoint));
        }
        for (const segKey of this.segments) {
            parts.push(segKey);
        }
        return parts.join('');
    }

    deserialize(serialized: string): void {
        this.segments.clear();
        if (!serialized) {
            this.initializeStartPoint();
            return;
        }

        const compact = stripSeparators(serialized);
        let startFound = false;
        let i = 0;
        // An optional start point makes the length ≡ 2 (mod 4); segments are 4 chars.
        if (compact.length % 4 === 2) {
            try {
                this.startPoint = parsePointKey(compact.slice(0, 2));
                startFound = true;
            } catch {
                // Invalid, skip
            }
            i = 2;
        }
        for (; i + 4 <= compact.length; i += 4) {
            this.segments.add(compact.slice(i, i + 4));
        }

        if (!startFound) {
            this.initializeStartPoint();
        }
    }
}
